// validation/usbGuard.js — frontend twin of proxy-server/ingest/usbGuard.js
// (the source of truth; pinned by usbGuardParity.test.js). ONE place that decides
// which findings on a request-only (adb logcat, `captureSource:'logcat'`) row are
// really "the response is not visible", and downgrades them to info.
//
// Used by AdLifecycleView.buildSlotLifecycles (the local fallback of the proxy's
// lifecycle answer) where slot issues and inherited GAM-row issues are built.
// Browser difference: the proxy's USB_GUARD=off kill switch is a server .env
// knob; here pass `{ enabled: false }` in opts / ctx instead (default on).
//
// A code allowlist, not a message regex (D9, docs/sdlc/slot-story-complete):
//   - RESPONSE_EVIDENCE_CODES  evidence is the response/body -> always downgrade
//   - TAIL_CODES               request-side but past logcat's ~4KB cut -> downgrade only when truncated
//   - CONDITIONAL_CODES        needs the APS response -> downgrade when truncated or APS absent
//   - KEEP_CODES / KEEP_PREFIXES  evidence is the URL itself or a capture fact -> never touched
//   - an unclassified code is kept (fails closed).
// Uncoded GAM request findings are downgraded only when truncated AND the field
// is a tail param AND the finding says the param is missing.
//
// Never throws; unknown input is returned untouched.

export const USB_NOT_VISIBLE = 'usb_not_visible';
const MSG_RESPONSE = 'Not visible in USB capture: this finding needs the response, which logcat does not carry.';
const MSG_TRUNCATED = 'Not visible in USB capture: the URL was cut by logcat, so this param may sit past the cut.';

export const RESPONSE_EVIDENCE_CODES = new Set([
  'gam_no_fill',
  'aps_not_captured',
  'aps_http_error',
  'prebid_bidder_timeout',
  'hb_pb_mismatch',
  'prebid_sentinel_size',
  'prebid_malformed_1x1',
  'prebid_winner_size_mismatch',
  'prebid_creative_size_mismatch',
  'prebid_size_not_in_slot',
  'aps_size_mismatch',
  'aps_size_partial',
  'banner_autoplay_sound_on',
  'soundOn',
  // validateGAMRequest response-derived creative checks
  'malformed_creative_1x1',
  'creative_size_mismatch',
  // joined to its slot by sigh from the GAM response
  'mediation_load_failed',
  // joined to its slot by the GAM response's bid_id
  'meta_test_mode_mismatch',
  // sdkBidding/placements.js placementChain: evidence is the pubsetting body, the GAM response
  // rendering_data and the Meta response, none of which logcat carries
  'meta_placement_chain_mismatch',
  'meta_placement_unknown',
  'meta_placement_unit_mismatch',
  'meta_placement_format_mismatch',
  // evaluateConfigPlacements: evidence is the pubsetting body and the imported report, neither of
  // which logcat carries (a USB-only session resolves no mapping and raises none of these)
  'meta_placement_config_unknown',
  'meta_placement_not_in_capture',
  'meta_placement_dead',
]);

// Request-side findings whose evidence can sit past the logcat cut.
export const TAIL_CODES = new Set([
  'secure_signal_adapter_failed',
  'truncated_value', // hygiene: a dangling %-escape at the end of a logcat-cut URL IS the cut
  'cust_params_empty_value', // hygiene: a cut can leave the last cust_params key without its value
]);

// Needs the APS response; on logcat that is absent, and a cut cust_params side
// hides amzn_b. Downgrade when either holds.
export const CONDITIONAL_CODES = new Set(['amzn_b_missing']);

export const KEEP_CODES = new Set([
  'ingest_request_only',
  'ingest_url_truncated',
  USB_NOT_VISIBLE,
  'no_impression',                    // already info; pinned by a test
  'mediation_impression_ambiguous',
  'gam_row_issue',                    // slot-level wrapper for an uncoded GAM row issue
  'double_encoded',                   // URL-level encoding fault
  'gam_rdp_id_leak',                  // URL-level privacy faults
  'gam_lat_rdid_leak',
  'gam_ms_missing',
  // hygiene/hygieneChecks.js: URL-evidence hygiene findings (double_encoded is above)
  'literal_null_value',
  'empty_required_value',
  'duplicate_query_key',
  'iu_whitespace',
  'under_encoded_nested_value',
  'unencoded_nested_url',
  'activeview_not_viewable',          // lifecycle/activeView.js: evidence is a ping URL param (never on logcat slots)
  'aps_chaos',                        // info: APS answered by a Chaos rule — proxy-only, never on logcat rows
  'us_privacy_opt_out',               // hygiene: the us_privacy value itself is on the URL; present = real
]);

// Families owned by other request types; their evidence is the request itself
// (or, for sdk_bidding_*, SDK telemetry that is request-only by nature).
export const KEEP_PREFIXES = ['ortb.', 'fw_', 'ss_fw_', 'video_', 'sdk_bidding_', 'tracker.'];

// Request types whose verdicts judge the BODY and the bid RESPONSE; logcat carries neither.
export const BODY_DRIVEN_TYPES = new Set(['prebid', 'amazon', 'amazon_video', 'ima']);

// Params that sit after the cut on a GAM URL.
export const TAIL_FIELDS = new Set([
  'a3p', 'gpp', 'gpp_sid', 'gdpr', 'gdpr_consent', 'us_privacy', 'ppid',
  'omid_p', 'omid_v', 'description_url', 'g', 'attmas', 'lmt',
]);

const FAILING_LEVELS = new Set(['fail', 'warn', 'error', 'warning', 'required', 'recommended']);

const MISSING_RE = /\b(missing|absent|not present|not forwarded)\b/i;

function levelOf(i) {
  return (i && (i.level || i.severity)) || '';
}
function msgOf(i) {
  return (i && (i.note || i.message || i.msg)) || '';
}

/** 'downgrade' | 'keep' | 'unclassified' for a CODE. */
export function classifyCode(code) {
  if (typeof code !== 'string' || !code) return 'unclassified';
  if (RESPONSE_EVIDENCE_CODES.has(code) || TAIL_CODES.has(code) || CONDITIONAL_CODES.has(code)) return 'downgrade';
  if (KEEP_CODES.has(code)) return 'keep';
  if (KEEP_PREFIXES.some((p) => code.startsWith(p))) return 'keep';
  return 'unclassified';
}

function isTailField(field) {
  const f = String(field || '').toLowerCase();
  return f.startsWith('cust_params') || TAIL_FIELDS.has(f);
}

/** Why (if at all) is this issue downgraded? -> 'response' | 'truncated' | null */
function downgradeReason(issue, ctx) {
  if (!issue || typeof issue !== 'object') return null;
  if (!FAILING_LEVELS.has(levelOf(issue))) return null;
  if (ctx.bodyDriven) return 'response';
  const code = issue.code;
  if (code) {
    if (RESPONSE_EVIDENCE_CODES.has(code)) return 'response';
    if (CONDITIONAL_CODES.has(code)) return (ctx.truncated || ctx.apsResponseAbsent) ? (ctx.truncated ? 'truncated' : 'response') : null;
    if (TAIL_CODES.has(code)) return ctx.truncated ? 'truncated' : null;
    return null; // keep-listed or unclassified: fail closed
  }
  // Uncoded GAM request finding: only a "missing" tail param on a cut URL.
  if (ctx.truncated && isTailField(issue.field) && MISSING_RE.test(msgOf(issue))) return 'truncated';
  return null;
}

function downgrade(issue, reason) {
  const text = reason === 'truncated' ? MSG_TRUNCATED : MSG_RESPONSE;
  const out = {
    ...issue,
    level: 'info',
    severity: 'info',
    code: USB_NOT_VISIBLE,
    originalLevel: levelOf(issue),
    originalCode: issue.code || null,
    originalMsg: msgOf(issue),
    reason,
    msg: text,
    message: text,
  };
  if (issue.note !== undefined) out.note = text;
  return out;
}

/**
 * Guard a bare issues array. ctx: { truncated, apsResponseAbsent, bodyDriven, enabled }.
 * Returns a new array; unmodified issues keep identity. Never throws.
 */
export function guardIssues(issues, ctx = {}) {
  try {
    if (!Array.isArray(issues) || (ctx && ctx.enabled === false)) return issues;
    return issues.map((i) => {
      const reason = downgradeReason(i, ctx || {});
      return reason ? downgrade(i, reason) : i;
    });
  } catch {
    return issues;
  }
}

export function isRequestOnlyRow(row) {
  return !!(row && row.captureSource === 'logcat');
}

function rowTruncated(row, opts) {
  if (opts && typeof opts.truncated === 'boolean') return opts.truncated;
  const list = Array.isArray(row && row.issues) ? row.issues : [];
  return list.some((i) => i && i.code === 'ingest_url_truncated');
}

/**
 * Apply the guard to a row IN PLACE (row.issues and row.validData.issues are
 * replaced with guarded arrays) and return it. opts: { truncated, requestOnly, enabled }.
 * A non-logcat row is untouched unless `requestOnly:true`.
 */
export function applyRequestOnlyGuard(row, opts = {}) {
  try {
    if (!row || typeof row !== 'object') return row;
    if (!(opts && opts.requestOnly) && !isRequestOnlyRow(row)) return row;
    if (opts && opts.enabled === false) return row;
    const ctx = {
      truncated: rowTruncated(row, opts),
      apsResponseAbsent: true,
      bodyDriven: BODY_DRIVEN_TYPES.has(row.requestType),
    };
    if (Array.isArray(row.issues)) row.issues = guardIssues(row.issues, ctx);
    const vd = row.validData;
    if (vd && typeof vd === 'object' && Array.isArray(vd.issues)) vd.issues = guardIssues(vd.issues, ctx);
    return row;
  } catch {
    return row;
  }
}
